<template>
  <div>
    <!-- Page Title -->
    <div class="content-header row">
      <div class="content-header-left col-md-9 col-12 mb-2">
        <div class="row breadcrumbs-top">
          <div class="col-12">
            <h2 class="content-header-title float-start mb-0">
              {{ $t('prePreview.creatives.title') }}
            </h2>
            <div class="breadcrumb-wrapper vs-breadcrumbs d-sm-block d-none col-12">
              <b-breadcrumb>
                <b-breadcrumb-item
                  :to="{ name: 'dashboard' }"
                >
                  {{ $t('common.dashboard') }}
                </b-breadcrumb-item>
                <b-breadcrumb-item
                  :to="{ name: 'pre-preview' }"
                >
                  {{ $t('prePreview.title') }}
                </b-breadcrumb-item>
                <b-breadcrumb-item active>
                  {{ $t('prePreview.creatives.title') }}
                </b-breadcrumb-item>
              </b-breadcrumb>
            </div>
          </div>
        </div>
      </div>
      <div class="content-header-right text-md-end col-md-3 col-12 d-md-block d-none">
        <div class="mb-1 breadcrumb-right">
          <b-button
            variant="primary"
            :to="{ name: 'pre-preview-upload' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="me-50"
            />
            {{ $t('prePreview.creatives.uploadNew') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <b-card class="mb-2">
      <b-row>
        <b-col
          md="3"
          sm="6"
          class="mb-1"
        >
          <b-form-group
            label="Search"
            label-for="search-input"
          >
            <b-form-input
              v-model="filters.search"
              id="search-input"
              placeholder="Search creatives..."
              @input="debounceSearch"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="2"
          sm="6"
          class="mb-1"
        >
          <b-form-group
            label="Type"
            label-for="type-select"
          >
            <b-form-select
              v-model="filters.type"
              id="type-select"
              :options="typeOptions"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="2"
          sm="6"
          class="mb-1"
        >
          <b-form-group
            label="AI Review Status"
            label-for="ai-review-status-select"
          >
            <b-form-select
              v-model="filters.aiReviewStatus"
              id="ai-review-status-select"
              :options="aiReviewStatusOptions"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          sm="12"
          class="mb-1"
        >
          <b-form-group
            label="Date Range"
            label-for="date-range"
          >
            <b-form-input
              v-model="filters.dateRange"
              id="date-range"
              type="date"
              placeholder="Select date range..."
              @input="onDateChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="selectAll"
              :indeterminate="indeterminate"
              @change="toggleSelectAll"
            >
              {{ $t('common.selectAll') }}
            </b-form-checkbox>
            <span
              v-if="selectedCreatives && selectedCreatives.length > 0"
              class="ms-1 text-muted"
            >
              ({{ selectedCreatives.length }} {{ $t('common.selected') }})
            </span>
          </div>
          <div
            v-if="selectedCreatives && selectedCreatives.length > 0"
            class="d-flex"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              class="me-1"
              :disabled="loading"
              @click="bulkResubmit"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="14"
              />
              {{ $t('prePreview.creatives.actions.resubmit') }}
            </b-button>
            <b-button
              variant="success"
              size="sm"
              class="mx-1"
              :disabled="loading"
              @click="openPreReviewModal"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="14"
              />
              Submit for Review
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              class="me-1"
              :disabled="loading"
              @click="bulkDelete"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
              {{ $t('common.delete') }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Data Table -->
    <b-card>
      <div
        v-if="loading && !creatives.length"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
        <p class="mt-2 text-muted">
          {{ $t('common.loading') }}
        </p>
      </div>

      <div
        v-else-if="!loading && !creatives.length"
        class="text-center py-5"
      >
        <feather-icon
          icon="ImageIcon"
          size="48"
          class="text-muted mb-2"
        />
        <h5 class="text-muted">
          {{ $t('prePreview.creatives.empty.title') }}
        </h5>
        <p class="text-muted mb-3">
          {{ $t('prePreview.creatives.empty.description') }}
        </p>
        <b-button
          variant="primary"
          :to="{ name: 'pre-preview-upload' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="me-50"
          />
          {{ $t('prePreview.creatives.uploadFirst') }}
        </b-button>
      </div>

      <b-table
        v-else
        :items="creatives"
        :fields="tableFields"
        :busy="loading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive
        hover
        show-empty
        @sort-changed="onSortChanged"
      >
        <!-- Selection checkbox -->
        <template #cell(selected)="{ item }">
          <b-form-checkbox
            v-model="selectedCreatives"
            :value="item.id"
            @change="updateSelectAll"
          />
        </template>

        <!-- Preview/Thumbnail -->
        <template #cell(preview)="{ item }">
          <div class="d-flex align-items-center">
            <div
              class="creative-preview me-3"
              @click="showPreview(item)"
            >
              <b-img
                v-if="!isVideoItem(item)"
                :src="getCreativeImageUrl(item)"
                :alt="item.filename || item.name"
                :data-item-id="item.id"
                class="creative-thumbnail"
                fluid
                @error="onImageError"
                @load="onImageLoad"
              />
              <div
                v-else
                class="video-thumbnail-wrapper"
              >
                <b-img
                  :src="getCreativeImageUrl(item)"
                  :alt="item.filename || item.name"
                  :data-item-id="item.id"
                  class="creative-thumbnail video-thumbnail"
                  fluid
                  @error="onVideoThumbnailError(item, $event)"
                  @load="onImageLoad"
                />
                <div class="play-overlay">
                  <feather-icon
                    icon="PlayIcon"
                    size="20"
                    class="text-white"
                  />
                </div>
                <div class="video-type-badge">
                  <small class="text-white">VIDEO</small>
                </div>
              </div>
            </div>
            <div class="creative-info">
              <feather-icon
                v-if="isVideoItem(item)"
                icon="VideoIcon"
                size="16"
                class="text-primary me-1"
              />
              <feather-icon
                v-else
                icon="ImageIcon"
                size="16"
                class="text-success me-1"
              />
              <span class="text-capitalize">{{ isVideoItem(item) ? 'video' : 'image' }}</span>
            </div>
          </div>
        </template>

        <!-- Name -->
        <template #cell(name)="{ item }">
          <div>
            <span
              class="font-weight-semibold text-truncate d-block"
              style="max-width: 200px"
              :title="item.name"
            >
              {{ item.filename || item.name }}
            </span>
            <small class="text-muted">
              {{ formatFileSize(item.fileSize || item.file_size || item.size) }}
            </small>
          </div>
        </template>

        <!-- AI Review Status -->
        <template #cell(aiReviewStatus)="{ item }">
          <b-badge
            :variant="getAiReviewStatusVariant(item)"
            class="text-capitalize"
          >
            {{ getAiReviewStatusText(item) }}
          </b-badge>
          <div
            v-if="item.preReviewStatus === 'rejected' && item.rejectReasons && Array.isArray(item.rejectReasons) && item.rejectReasons.length"
            class="mt-1"
          >
            <b-button
              variant="link"
              size="sm"
              class="p-0 text-danger"
              @click="showRejectReasons(item)"
            >
              <feather-icon
                icon="AlertCircleIcon"
                size="14"
              />
              {{ $t('prePreview.creatives.viewReasons') }}
            </b-button>
          </div>
        </template>

        <!-- Upload Date -->
        <template #cell(uploadDate)="{ item }">
          <div>
            <span>{{ formatDate(item.createdAt || item.created_at || item.uploadDate) }}</span>
            <br>
            <small class="text-muted">
              {{ formatTime(item.createdAt || item.created_at || item.uploadDate) }}
            </small>
          </div>
        </template>

        <!-- Actions -->
        <template #cell(actions)="{ item }">
          <b-dropdown
            variant="link"
            toggle-class="text-decoration-none"
            no-caret
            right
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="text-body"
              />
            </template>
            <b-dropdown-item @click="showPreview(item)">
              <feather-icon
                icon="EyeIcon"
                class="me-50"
              />
              {{ $t('prePreview.creatives.actions.viewDetails') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="item.preReviewStatus === 'rejected' || item.preReviewStatus === 'unavailable'"
              @click="resubmitForReview(item)"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="me-50"
              />
              {{ $t('prePreview.creatives.actions.resubmit') }}
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item
              variant="danger"
              @click="deleteCreative(item)"
            >
              <feather-icon
                icon="TrashIcon"
                class="me-50"
              />
              {{ $t('common.delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </template>

        <!-- Loading state -->
        <template #table-busy>
          <div class="text-center text-muted my-2">
            <b-spinner class="align-middle" />
            <strong class="ms-2">{{ $t('common.loading') }}...</strong>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row
        v-if="totalCreatives > pageSize"
        class="mt-4"
      >
        <b-col class="d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCreatives"
            :per-page="pageSize"
            :limit="7"
            class="mb-0 pagination-lg"
            @change="onPageChange"
          />
        </b-col>
      </b-row>
    </b-card>

    <!-- Preview Modal -->
    <b-modal
      v-model="showPreviewModal"
      id="preview-modal"
      :title="previewItem ? previewItem.name : ''"
      size="lg"
      hide-footer
      centered
    >
      <div
        v-if="previewItem"
        class="text-center"
      >
        <video
          v-if="isVideoItem(previewItem)"
          :src="getVideoUrl(previewItem)"
          controls
          class="w-100"
          style="max-height: 70vh"
          @error="onVideoError"
          @loadstart="onVideoLoadStart"
        />
        <b-img
          v-else
          :src="getCreativeImageUrl(previewItem)"
          :alt="previewItem.filename || previewItem.name"
          fluid
          class="w-100"
          style="max-height: 70vh"
          @error="onImageError"
        />
        <div class="mt-3 text-start">
          <h5>{{ $t('prePreview.creatives.details.title') }}</h5>
          <table class="table table-sm">
            <tbody>
              <tr>
                <td class="font-weight-semibold">
                  {{ $t('prePreview.creatives.details.name') }}:
                </td>
                <td>{{ previewItem.filename || previewItem.name }}</td>
              </tr>
              <tr>
                <td class="font-weight-semibold">
                  {{ $t('prePreview.creatives.details.type') }}:
                </td>
                <td class="text-capitalize">
                  {{ isVideoItem(previewItem) ? 'video' : 'image' }}
                </td>
              </tr>
              <tr>
                <td class="font-weight-semibold">
                  {{ $t('prePreview.creatives.details.size') }}:
                </td>
                <td>{{ formatFileSize(previewItem.fileSize || previewItem.file_size || previewItem.size) }}</td>
              </tr>
              <tr>
                <td class="font-weight-semibold">
                  {{ $t('prePreview.creatives.details.uploadDate') }}:
                </td>
                <td>{{ formatDateTime(previewItem.createdAt || previewItem.created_at || previewItem.uploadDate || previewItem.upload_date) }}</td>
              </tr>
              <tr>
                <td class="font-weight-semibold">
                  AI Review Status:
                </td>
                <td>
                  <b-badge :variant="getAiReviewStatusVariant(previewItem)">
                    {{ getAiReviewStatusText(previewItem) }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- Reject Reasons Modal -->
    <b-modal
      v-model="showRejectReasonsModal"
      id="reject-reasons-modal"
      :title="$t('prePreview.creatives.rejectReasons.title')"
      hide-footer
      centered
    >
      <div v-if="rejectReasonsItem">
        <h6 class="text-danger mb-3">
          {{ rejectReasonsItem.name }}
        </h6>
        <ul class="list-unstyled">
          <li
            v-for="(reason, index) in rejectReasonsItem.rejectReasons"
            :key="index"
            class="mb-2"
          >
            <feather-icon
              icon="XCircleIcon"
              size="16"
              class="text-danger me-2"
            />
            {{ reason }}
          </li>
        </ul>
      </div>
    </b-modal>

    <!-- Pre-Review Modal -->
    <b-modal
      v-model="showPreReviewModal"
      id="pre-review-modal"
      title="Submit for Pre-Review"
      ok-title="Submit"
      cancel-title="Cancel"
      centered
      @ok="submitForPreReview"
      @cancel="closePreReviewModal"
    >
      <div>
        <p class="mb-3">
          Submit {{ selectedCreatives.length }} creative(s) for TikTok pre-review.
        </p>
        <div class="alert alert-info p-2">
          <small>
            <strong>Materials to be reviewed:</strong><br>
            • {{ selectedCreatives.length }} creative(s)<br>
            • <span v-if="preReviewForm.adText.trim()">Ad text (included)</span>
            <span
              v-else
              class="text-muted"
            >Ad text (optional - not included)</span><br>
            • <span v-if="preReviewForm.landingPageUrl.trim()">Landing page URL (included)</span>
            <span
              v-else
              class="text-muted"
            >Landing page URL (optional - not included)</span>
          </small>
        </div>

        <b-form-group
          label="Ad Text (Optional)"
          label-for="ad-text-input"
        >
          <b-form-input
            v-model="preReviewForm.adText"
            id="ad-text-input"
            placeholder="Enter ad text content..."
            maxlength="100"
          />
          <small class="text-muted">{{ preReviewForm.adText.length }}/100 characters</small>
        </b-form-group>

        <b-form-group
          label="Landing Page URL (Optional)"
          label-for="landing-page-input"
        >
          <b-form-input
            v-model="preReviewForm.landingPageUrl"
            id="landing-page-input"
            type="url"
            placeholder="https://example.com"
          />
        </b-form-group>

        <b-form-group
          label="Location"
          label-for="location-select"
        >
          <b-form-select
            v-model="preReviewForm.location"
            id="location-select"
            :options="locationOptions"
          />
        </b-form-group>

        <b-form-group>
          <b-form-checkbox
            v-model="preReviewForm.isEcommerce"
          >
            E-commerce Campaign
          </b-form-checkbox>
        </b-form-group>
      </div>
    </b-modal>

    <!-- Submission Result Modal -->
    <b-modal
      v-model="showSubmissionResultModal"
      id="submission-result-modal"
      title=""
      hide-header
      hide-footer
      centered
      size="lg"
    >
      <div v-if="preReviewSubmissionResult">
        <!-- Success Header -->
        <div class="text-center mb-4">
          <div class="success-icon mb-3">
            <feather-icon
              icon="CheckCircleIcon"
              size="64"
              class="text-success"
            />
          </div>
          <h3 class="text-success mb-2">
            Successfully Submitted!
          </h3>
          <p class="text-muted">
            {{ preReviewSubmissionResult.message }}
          </p>
        </div>

        <!-- Task Information -->
        <b-card
          class="mb-4"
          bg-variant="light"
        >
          <b-row>
            <b-col cols="6">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="HashIcon"
                  size="16"
                  class="me-2 text-primary"
                />
                <strong>Task ID:</strong>
              </div>
              <code class="text-primary">{{ preReviewSubmissionResult.taskId }}</code>
            </b-col>
            <b-col cols="6">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                  class="me-2 text-warning"
                />
                <strong>Status:</strong>
              </div>
              <b-badge variant="warning">
                {{ preReviewSubmissionResult.status }}
              </b-badge>
            </b-col>
          </b-row>
          <hr class="my-3">
          <b-row>
            <b-col cols="6">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="LayersIcon"
                  size="16"
                  class="me-2 text-info"
                />
                <strong>Materials:</strong>
              </div>
              <span>{{ preReviewSubmissionResult.submittedCount }} creative(s)</span>
            </b-col>
            <b-col cols="6">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="CalendarIcon"
                  size="16"
                  class="me-2 text-secondary"
                />
                <strong>Created:</strong>
              </div>
              <span>{{ formatDateTime(preReviewSubmissionResult.createdAt) }}</span>
            </b-col>
          </b-row>
        </b-card>

        <!-- Materials List -->
        <div class="mb-4">
          <h5 class="mb-3">
            <feather-icon
              icon="ImageIcon"
              size="18"
              class="me-2"
            />
            Submitted Materials
          </h5>
          <div class="materials-list">
            <div
              v-for="material in preReviewSubmissionResult.materials"
              :key="material.id"
              class="material-item p-3 border rounded mb-2"
            >
              <b-row align-v="center">
                <b-col cols="auto">
                  <div class="material-icon">
                    <feather-icon
                      icon="ImageIcon"
                      size="24"
                      class="text-primary"
                    />
                  </div>
                </b-col>
                <b-col>
                  <div class="material-info">
                    <h6 class="mb-1">
                      {{ material.filename }}
                    </h6>
                    <div class="d-flex align-items-center">
                      <b-badge
                        variant="primary"
                        class="me-2"
                      >
                        {{ material.type }}
                      </b-badge>
                      <small class="text-muted">TikTok ID: {{ material.tiktokMaterialId }}</small>
                    </div>
                  </div>
                </b-col>
                <b-col cols="auto">
                  <feather-icon
                    icon="CheckIcon"
                    size="20"
                    class="text-success"
                  />
                </b-col>
              </b-row>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="text-center">
          <b-button
            variant="primary"
            class="me-2"
            @click="viewTaskHistory"
          >
            <feather-icon
              icon="HistoryIcon"
              size="16"
              class="me-1"
            />
            View Task History
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="closeSubmissionResultModal"
          >
            Close
          </b-button>
        </div>

        <!-- Next Steps Info -->
        <div class="mt-4 p-3 bg-secondary bg-opacity-10 rounded">
          <div class="d-flex align-items-start">
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="me-2 text-info mt-1"
            />
            <div>
              <strong class="text-info">What's Next?</strong>
              <p class="mb-0 small text-muted mt-1">
                Your creative materials are now being reviewed by TikTok.
                You'll receive updates as the review progresses.
                This usually takes 5-10 minutes to complete.
              </p>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from 'lodash'
import CreativeService from '@/services/CreativeService'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BTable,
  BSpinner,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  BModal,
  BImg,
  BBreadcrumb,
  BBreadcrumbItem,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BTable,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BModal,
    BImg,
    BBreadcrumb,
    BBreadcrumbItem,
  },
  data() {
    return {
      loading: false,
      filters: {
        search: '',
        type: '',
        aiReviewStatus: '',
        dateRange: null,
      },
      sortBy: 'uploadDate',
      sortDesc: true,
      currentPage: 1,
      pageSize: 10,

      // Selection
      selectedCreatives: [],
      selectAll: false,
      indeterminate: false,

      // Modals
      showPreviewModal: false,
      previewItem: null,
      showRejectReasonsModal: false,
      rejectReasonsItem: null,
      showPreReviewModal: false,
      showSubmissionResultModal: false,

      // Pre-review form
      preReviewForm: {
        adText: '',
        landingPageUrl: '',
        location: 'US',
        isEcommerce: false,
      },

      // Submission result data
      preReviewSubmissionResult: null,

    }
  },
  computed: {
    ...mapState('creativeUpload', [
      'creativesList',
      'creativesTotal',
    ]),
    ...mapGetters('creativeUpload', [
      // Add getters if needed
    ]),

    creatives() {
      let filteredList = this.creativesList || []

      // Apply frontend type filtering based on our enhanced video detection
      if (this.filters.type) {
        filteredList = filteredList.filter(item => {
          const isVideo = this.isVideoItem(item)
          const matchesFilter = (this.filters.type === 'video' && isVideo)
                               || (this.filters.type === 'image' && !isVideo)

          // Debug info removed for production

          return matchesFilter
        })
      }

      // Apply frontend search filtering for type-specific searches
      if (this.filters.search && this.filters.search.trim() !== '') {
        const searchTerm = this.filters.search.toLowerCase().trim()

        filteredList = filteredList.filter(item => {
          // Handle special type searches
          if (searchTerm === 'video' || searchTerm === 'videos') {
            const isVideo = this.isVideoItem(item)
            return isVideo
          } if (searchTerm === 'image' || searchTerm === 'images') {
            const isVideo = this.isVideoItem(item)
            return !isVideo
          }
          // Regular search in filename, original_filename, and id
          const filename = (item.filename || '').toLowerCase()
          const originalFilename = (item.original_filename || '').toLowerCase()
          const id = (item.id || '').toLowerCase()
          const matches = filename.includes(searchTerm) || originalFilename.includes(searchTerm) || id.includes(searchTerm)
          return matches
        })
      }

      return filteredList
    },

    totalCreatives() {
      // If we're filtering by type on frontend, return filtered count
      if (this.filters.type) {
        return this.creatives.length
      }
      return this.creativesTotal || 0
    },

    tableFields() {
      return [
        {
          key: 'selected',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          key: 'preview',
          label: this.$t('prePreview.creatives.table.preview'),
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: this.$t('prePreview.creatives.table.name'),
          sortable: true,
        },
        {
          key: 'aiReviewStatus',
          label: 'AI Review Status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'uploadDate',
          label: this.$t('prePreview.creatives.table.uploadDate'),
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'actions',
          label: this.$t('common.actions'),
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ]
    },

    typeOptions() {
      return [
        { value: '', text: 'All Types' },
        { value: 'video', text: 'Video' },
        { value: 'image', text: 'Image' },
      ]
    },

    aiReviewStatusOptions() {
      return [
        { value: '', text: 'All Review Status' },
        { value: 'uploaded', text: 'Uploaded' },
        { value: 'uploading', text: 'Uploading' },
        { value: 'ready_for_review', text: 'Ready for Review' },
        { value: 'ai_reviewing', text: 'AI Reviewing' },
        { value: 'ai_approved', text: 'AI Approved' },
        { value: 'ai_rejected', text: 'AI Rejected' },
        { value: 'upload_failed', text: 'Upload Failed' },
        { value: 'unavailable', text: 'Unavailable' },
      ]
    },

    locationOptions() {
      return [
        { value: 'US', text: 'United States' },
        // Add more locations as needed
      ]
    },
  },
  watch: {
    selectedCreatives() {
      this.updateSelectAll()
    },
  },
  created() {
    // Check if there's a creativeId in the query params to highlight/filter
    if (this.$route.query.creativeId) {
      // You can use this creativeId to filter or highlight the specific creative
      // For now, just log it - you may want to add filtering or highlighting logic later
    }

    this.debounceSearch = debounce(this.onFilterChange, 500)
    this.fetchCreativesList()
  },
  methods: {
    ...mapActions('creativeUpload', [
      'fetchCreatives',
      'deleteCreative',
      'bulkDeleteCreatives',
      'setFilters',
      'setPage',
      'setSort',
      'submitCreativesForReview',
    ]),

    async fetchCreativesList() {
      this.loading = true
      try {
        // Map the combined AI Review Status filter to backend parameters
        let statusFilter = ''
        let preReviewStatusFilter = ''

        switch (this.filters.aiReviewStatus) {
          case 'uploaded':
            statusFilter = 'uploaded'
            break
          case 'uploading':
            statusFilter = 'processing'
            break
          case 'ready_for_review':
            statusFilter = 'completed'
            break
          case 'ai_reviewing':
            preReviewStatusFilter = 'pending'
            break
          case 'ai_approved':
            preReviewStatusFilter = 'approved'
            break
          case 'ai_rejected':
            preReviewStatusFilter = 'rejected'
            break
          case 'upload_failed':
            statusFilter = 'failed'
            break
          case 'unavailable':
            preReviewStatusFilter = 'unavailable'
            break
          default:
            // '' (empty) means all statuses - no filters applied
            break
        }

        const filterParams = {
          search: this.filters.search,
          // type: this.filters.type, // Handle type filtering on frontend due to enhanced video detection
          status: statusFilter,
          preReviewStatus: preReviewStatusFilter,
          dateRange: this.filters.dateRange,
        }

        await this.setFilters(filterParams)
        await this.setPage(this.currentPage)
        await this.fetchCreatives()

        if (this.creatives.length > 0) {
          // Check for newly uploaded images (last 24 hours)
          const recentImages = this.creatives.filter(c => c.status === 'READY'
            && c.createdAt
            && new Date(c.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000))
          if (recentImages.length > 0) {
            // Recent images found - notification logic removed for production
          }
        }
      } catch (error) {
        // Check if it's a 404 (API not implemented yet)
        if (error.response?.status === 404) {
          // Don't show error toast for 404 - just let it show empty state
          return
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: error.message || 'Failed to load creatives',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.selectedCreatives = []
      this.fetchCreativesList()
    },

    onDateChange() {
      this.onFilterChange()
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchCreativesList()
    },

    onPageChange(page) {
      this.currentPage = page
      this.fetchCreativesList()
    },

    // Selection methods
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedCreatives = this.creatives.map(item => item.id)
      } else {
        this.selectedCreatives = []
      }
    },

    updateSelectAll() {
      const selectedCount = this.selectedCreatives.length
      const totalCount = this.creatives.length

      this.selectAll = selectedCount === totalCount && totalCount > 0
      this.indeterminate = selectedCount > 0 && selectedCount < totalCount
    },

    // Preview methods
    showPreview(item) {
      // Debug preview data removed for production
      this.previewItem = item
      this.showPreviewModal = true
    },

    showRejectReasons(item) {
      this.rejectReasonsItem = item
      this.showRejectReasonsModal = true
    },

    // Action methods
    async resubmitForReview(item) {
      try {
        // Navigate to upload page with pre-filled data for resubmission
        this.$router.push({
          name: 'pre-preview-upload',
          query: { resubmit: item.id },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: error.message || this.$t('prePreview.creatives.errors.resubmitFailed'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async deleteCreative(item) {
      const result = await this.$swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('prePreview.creatives.confirmDelete', { name: item.name }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#ea5455',
      })

      if (result.isConfirmed) {
        try {
          await this.deleteCreative(item.id)
          this.fetchCreativesList()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              text: this.$t('prePreview.creatives.deleteSuccess'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.error'),
              text: error.message || this.$t('prePreview.creatives.errors.deleteFailed'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      }
    },

    async bulkResubmit() {
      if (!this.selectedCreatives.length) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: 'Please select at least one creative to resubmit',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Check if all selected creatives can be resubmitted
      const selectedItems = this.creatives.filter(creative => this.selectedCreatives.includes(creative.id))

      const invalidItems = selectedItems.filter(item => !['rejected', 'unavailable'].includes(item.preReviewStatus))

      if (invalidItems.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: `${invalidItems.length} creative(s) cannot be resubmitted. Only rejected or unavailable creatives can be resubmitted.`,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const result = await this.$swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('prePreview.creatives.confirmBulkResubmit', { count: this.selectedCreatives.length }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('prePreview.creatives.actions.resubmit'),
        cancelButtonText: this.$t('common.cancel'),
      })

      if (result.isConfirmed) {
        try {
          // Navigate to upload page with multiple creative IDs for bulk resubmission
          this.$router.push({
            name: 'pre-preview-upload',
            query: { bulkResubmit: this.selectedCreatives.join(',') },
          })
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.error'),
              text: 'Failed to navigate to resubmission page',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      }
    },

    async bulkDelete() {
      if (!this.selectedCreatives.length) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: 'Please select at least one creative to delete',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const result = await this.$swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('prePreview.creatives.confirmBulkDelete', { count: this.selectedCreatives.length }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#ea5455',
      })

      if (result.isConfirmed) {
        this.loading = true
        const selectedIds = [...this.selectedCreatives]

        try {
          await this.bulkDeleteCreatives(selectedIds)
          this.selectedCreatives = []
          await this.fetchCreativesList()

          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              text: this.$t('prePreview.creatives.bulkDeleteSuccess'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } catch (error) {
          // Check if error contains information about partial failures
          let errorMessage = this.$t('prePreview.creatives.errors.bulkDeleteFailed')

          if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.message) {
            errorMessage = error.message
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.error'),
              text: errorMessage,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })

          // Refresh the list to show current state
          await this.fetchCreativesList()
        } finally {
          this.loading = false
        }
      }
    },

    // Utility methods
    getStatusVariant(status) {
      const variants = {
        uploaded: 'light-info',
        processing: 'light-warning',
        completed: 'light-success',
        failed: 'light-danger',
      }
      return variants[status] || 'light-secondary'
    },

    getPreReviewStatusVariant(status) {
      const variants = {
        pending: 'light-warning',
        approved: 'light-success',
        rejected: 'light-danger',
        unavailable: 'light-secondary',
      }
      return variants[status] || 'light-secondary'
    },

    formatFileSize(bytes) {
      if (!bytes) return 'N/A'
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString()
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleTimeString()
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    },

    // Pre-review methods
    openPreReviewModal() {
      if (this.selectedCreatives.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.warning'),
            text: 'Please select at least one creative to submit for review',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      this.showPreReviewModal = true
    },

    closePreReviewModal() {
      this.showPreReviewModal = false
      this.resetPreReviewForm()
    },

    resetPreReviewForm() {
      this.preReviewForm = {
        adText: '',
        landingPageUrl: '',
        location: 'US',
        isEcommerce: false,
      }
    },

    async submitForPreReview() {
      try {
        // Get the full creative objects for the selected IDs
        const selectedCreativeObjects = this.creatives.filter(creative => this.selectedCreatives.includes(creative.id))

        const reviewParams = {
          creatives: selectedCreativeObjects,
          ad_text: this.preReviewForm.adText,
          landing_page_url: this.preReviewForm.landingPageUrl,
          location: this.preReviewForm.location,
          is_ecommerce: this.preReviewForm.isEcommerce,
        }

        const result = await this.submitCreativesForReview(reviewParams)

        // Store the submission result for the success modal
        this.preReviewSubmissionResult = {
          success: true,
          submittedCount: selectedCreativeObjects.length,
          taskId: result.task_id,
          status: result.status,
          message: result.message,
          createdAt: result.created_at,
          materials: selectedCreativeObjects.map(creative => ({
            id: creative.id,
            filename: creative.filename,
            type: this.isVideoItem(creative) ? 'VIDEO' : 'IMAGE',
            tiktokMaterialId: creative.metadata?.tiktok_material_id,
          })),
        }

        // Show the success modal
        this.showSubmissionResultModal = true

        this.closePreReviewModal()
        this.selectedCreatives = []
        this.fetchCreativesList()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            text: error.message || 'Failed to submit creatives for pre-review',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    // Success modal methods
    closeSubmissionResultModal() {
      this.showSubmissionResultModal = false
      this.preReviewSubmissionResult = null
    },

    viewTaskHistory() {
      this.closeSubmissionResultModal()
      // Navigate to task history page
      this.$router.push({ name: 'pre-preview-tasks' })
    },

    // Status text methods
    getStatusText(status) {
      if (!status) return 'Unknown'

      const statusMap = {
        uploaded: 'Uploaded',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
        ready: 'Ready',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        error: 'Error',
        success: 'Success',
      }

      return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    },

    getPreReviewStatusText(status) {
      if (!status || status === 'undefined') return 'Not Submitted'

      const statusMap = {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        unavailable: 'Unavailable',
        submitted: 'Submitted',
        not_submitted: 'Not Submitted',
        in_review: 'In Review',
        processing: 'Processing',
      }

      return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    },

    // Combined AI Review Status methods
    getAiReviewStatusText(item) {
      if (!item) return 'Unknown'

      // Debug info removed for production

      // Check TikTok API format first (pre_review_result_list)
      if (item.metadata?.pre_review_result_list && Array.isArray(item.metadata.pre_review_result_list)) {
        const result = item.metadata.pre_review_result_list.find(r => r.material_id === item.material_id || r.material_id === item.metadata?.tiktok_material_id)
        if (result && result.pre_review_status) {
          const status = result.pre_review_status.toLowerCase()
          const preReviewMap = {
            pending: 'AI Reviewing',
            approved: 'AI Approved',
            rejected: 'AI Rejected',
            in_progress: 'AI Reviewing',
          }
          const mappedText = preReviewMap[status] || result.pre_review_status
          return mappedText
        }
      }

      // Check for direct pre_review_status field
      if (item.pre_review_status && item.pre_review_status !== 'undefined') {
        const status = item.pre_review_status.toLowerCase()
        const preReviewMap = {
          pending: 'AI Reviewing',
          approved: 'AI Approved',
          rejected: 'AI Rejected',
          unavailable: 'Unavailable',
        }
        const mappedText = preReviewMap[status] || item.pre_review_status
        return mappedText
      }

      // If has legacy pre-review status, use that as priority
      if (item.preReviewStatus && item.preReviewStatus !== 'undefined' && item.preReviewStatus !== 'not_submitted') {
        const preReviewMap = {
          pending: 'AI Reviewing',
          approved: 'AI Approved',
          rejected: 'AI Rejected',
          unavailable: 'Unavailable',
          submitted: 'AI Reviewing',
          in_review: 'AI Reviewing',
        }
        const preReviewText = preReviewMap[item.preReviewStatus.toLowerCase()]
        if (preReviewText) {
          return preReviewText
        }
      }

      // Fallback to upload status
      const uploadMap = {
        uploaded: 'Uploaded',
        processing: 'Uploading',
        completed: 'Ready for Review',
        failed: 'Upload Failed',
        ready: 'Ready for Review',
      }

      return uploadMap[item.status?.toLowerCase()] || 'Unknown'
    },

    getAiReviewStatusVariant(item) {
      if (!item) return 'light-secondary'

      // Check TikTok API format first (pre_review_result_list)
      if (item.metadata?.pre_review_result_list && Array.isArray(item.metadata.pre_review_result_list)) {
        const result = item.metadata.pre_review_result_list.find(r => r.material_id === item.material_id || r.material_id === item.metadata?.tiktok_material_id)
        if (result && result.pre_review_status) {
          const status = result.pre_review_status.toLowerCase()
          const preReviewVariants = {
            pending: 'light-warning',
            approved: 'light-success',
            rejected: 'light-danger',
            in_progress: 'light-warning',
          }
          return preReviewVariants[status] || 'light-secondary'
        }
      }

      // Check for direct pre_review_status field
      if (item.pre_review_status && item.pre_review_status !== 'undefined') {
        const status = item.pre_review_status.toLowerCase()
        const preReviewVariants = {
          pending: 'light-warning',
          approved: 'light-success',
          rejected: 'light-danger',
          unavailable: 'light-secondary',
        }
        return preReviewVariants[status] || 'light-secondary'
      }

      // If has legacy pre-review status, use that for coloring
      if (item.preReviewStatus && item.preReviewStatus !== 'undefined' && item.preReviewStatus !== 'not_submitted') {
        const preReviewVariants = {
          pending: 'light-warning',
          approved: 'light-success',
          rejected: 'light-danger',
          unavailable: 'light-secondary',
          submitted: 'light-warning',
          in_review: 'light-warning',
        }
        const variant = preReviewVariants[item.preReviewStatus.toLowerCase()]
        if (variant) return variant
      }

      // Fallback to upload status variants
      const uploadVariants = {
        uploaded: 'light-info',
        processing: 'light-warning',
        completed: 'light-primary',
        failed: 'light-danger',
        ready: 'light-primary',
      }

      return uploadVariants[item.status?.toLowerCase()] || 'light-secondary'
    },

    // Image handling methods
    getCreativeImageUrl(item) {
      if (!item) {
        return this.getPlaceholderImage('image')
      }

      // Handle video thumbnails differently
      if (this.isVideoItem(item)) {
        return this.getVideoThumbnailUrl(item)
      }

      // Clean debug logging for newly uploaded images (remove specific timestamp after testing)
      const isNewlyUploaded = item.filename && (
        item.filename.includes('_1759126277737')
        || (item.status === 'READY' && item.createdAt && new Date(item.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)) // Last 24 hours
      )
      if (isNewlyUploaded) {
        // Newly uploaded image detection - debug logic removed for production
      }

      // Debug info removed for production

      // Priority 0: Use TikTok CDN URL for newly uploaded images (from file_path field)
      if (isNewlyUploaded && item.file_path && item.file_path.includes('ibyteimg.com')) {
        return item.file_path
      }

      // Priority 1: TikTok CDN URL from file_path (where it's actually stored)
      if (item.file_path && item.file_path.includes('ibyteimg.com') && this.isValidImageUrl(item.file_path)) {
        return item.file_path
      }

      // Priority 2: TikTok CDN URL from metadata.tiktok_response.image_url (alternative location)
      if (item.metadata?.tiktok_response?.image_url && this.isValidImageUrl(item.metadata.tiktok_response.image_url)) {
        return item.metadata.tiktok_response.image_url
      }

      // Priority 3: Direct URL field (legacy support)
      if (item.url && this.isValidImageUrl(item.url)) {
        return item.url
      }

      // Priority 4: Backend API service URL (fallback)
      if (item.id) {
        try {
          const apiUrl = CreativeService.getCreativeImage(item.id)
          if (isNewlyUploaded) {
            // Debug logging removed for production
          }
          return apiUrl
        } catch (error) {
          // Error logging removed for production
        }
      }

      // Priority 5: Backend file_path (if it's a local server URL)
      if (item.file_path
          && this.isValidImageUrl(item.file_path)
          && (item.file_path.startsWith('/') || item.file_path.includes(window.location.hostname))) {
        return item.file_path
      }

      // Priority 6: Legacy properties (for backward compatibility)
      const legacyProps = ['imageUrl', 'image_url', 'thumbnailUrl', 'thumbnail_url', 'previewUrl', 'preview_url']
      const foundLegacyProp = legacyProps.find(prop => item[prop]
        && this.isValidImageUrl(item[prop])
        && !this.isTikTokCdnUrl(item[prop]))
      if (foundLegacyProp) {
        return item[foundLegacyProp]
      }

      // Fallback to placeholder
      return this.getPlaceholderImage(item.type || 'image')
    },

    // Helper method to validate image URLs
    isValidImageUrl(url) {
      if (!url || typeof url !== 'string') return false

      // Check for common image extensions
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const urlLower = url.toLowerCase()

      // If URL has image extension, it's likely valid
      if (imageExtensions.some(ext => urlLower.includes(ext))) {
        return true
      }

      // Special case for TikTok CDN URLs (they have image in the path)
      if (url.includes('ibyteimg.com') && url.includes('image')) {
        return true
      }

      // If URL starts with http/https and doesn't look like a video, assume it's valid
      if (url.startsWith('http') && !this.isVideoUrl(url)) {
        return true
      }

      // Check for data URLs (base64 images)
      if (url.startsWith('data:image/')) {
        return true
      }

      return false
    },

    // Helper method to check if URL is a video
    isVideoUrl(url) {
      if (!url) return false
      const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv']
      const urlLower = url.toLowerCase()
      return videoExtensions.some(ext => urlLower.includes(ext))
    },

    // Helper method to check if URL is from TikTok CDN
    isTikTokCdnUrl(url) {
      if (!url) return false
      return url.includes('ibyteimg.com') || url.includes('tiktokcdn.com') || url.includes('tiktok.com')
    },

    getPlaceholderImage(type) {
      if (type === 'video') {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iIzZGN0I4OCIvPgo8cGF0aCBkPSJNMjQgMjBMMzkgMzJMMjQgNDRWMjBaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K'
      }
      // Default image placeholder
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iI0Y4RjlGQSIgc3Ryb2tlPSIjRTJFOEYwIiBzdHJva2Utd2lkdGg9IjIiLz4KPHBhdGggZD0iTTE2IDQwTDI0IDMyTDMyIDQwTDQ4IDI0VjQ4SDE2VjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIyNCIgY3k9IjI0IiByPSI0IiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo='
    },

    onImageError(event) {
      // Replace broken image with placeholder
      const { target } = event
      const itemId = target.getAttribute('data-item-id')
      const item = itemId ? this.creatives.find(c => c.id === itemId) : null

      // Check if this is a recently uploaded image
      const isRecentlyUploaded = item?.status === 'READY'
        && item?.createdAt
        && new Date(item.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)

      if (isRecentlyUploaded) {
        // Recently uploaded image error - debug logic removed for production
      }

      // Error info removed for production

      // Check if this is a TikTok CDN URL that failed
      if (target.src.includes('ibyteimg.com') || target.src.includes('tiktokcdn.com')) {
        // Try to find alternative URL from the item data
        if (item) {
          const alternativeUrl = this.getAlternativeImageUrl(item)
          if (alternativeUrl && alternativeUrl !== target.src) {
            target.src = alternativeUrl
            return
          }
        }
      }

      // For newly uploaded images, try different backend API endpoints
      if (item && (target.src.includes('/api/creative/') || target.src.includes('/api/uploads/') || target.src.includes('/files/'))) {
        // Try backend endpoint variations based on your database schema
        const backendAlternatives = [
          `/api/creative/image/${item.id}`, // Current CreativeService endpoint
          `/api/creative/file/${item.id}`, // Alternative file endpoint
          `/api/creatives/${item.id}/image`, // RESTful endpoint
          `/api/creatives/${item.id}/thumbnail`, // Thumbnail endpoint
          `/api/uploads/image/${item.id}`, // Upload service endpoint
          `/files/${item.id}`, // Simple file serving
          `/static/uploads/${item.id}`, // Static file serving
          `/uploads/${item.filename}`, // Direct filename access (if files are stored by name)
        ]

        // Find current URL in the list and try the next one
        const currentUrlIndex = backendAlternatives.findIndex(url => target.src.includes(url))
        const nextUrl = backendAlternatives[currentUrlIndex + 1]

        if (nextUrl) {
          target.src = nextUrl
          return
        }

        // As a last resort, try the TikTok CDN URL if available
        if (item.url && item.url.includes('ibyteimg.com')) {
          target.src = item.url
          return
        }
      }

      // Try to determine the type from the closest row data
      const row = target.closest('tr')
      let type = 'image' // default type

      if (row) {
        // Look for type information in the row
        const typeCell = row.querySelector('[class*="type"]')
        if (typeCell && typeCell.textContent.toLowerCase().includes('video')) {
          type = 'video'
        }
      }

      const placeholderUrl = this.getPlaceholderImage(type)

      // Add a small delay to prevent infinite error loops
      setTimeout(() => {
        target.src = placeholderUrl
      }, 100)
    },

    // Get alternative image URL when TikTok CDN fails
    getAlternativeImageUrl(item) {
      if (!item) return null

      // Skip TikTok CDN URLs and try other sources
      const alternativeProps = ['imageUrl', 'thumbnailUrl', 'previewUrl']

      const foundAlternativeProp = alternativeProps.find(prop => item[prop]
        && !item[prop].includes('ibyteimg.com')
        && !item[prop].includes('tiktokcdn.com')
        && this.isValidImageUrl(item[prop]))
      if (foundAlternativeProp) {
        return item[foundAlternativeProp]
      }

      // Try API endpoint as fallback
      if (item.id) {
        try {
          return CreativeService.getCreativeImage(item.id)
        } catch (error) {
          // Error logging removed for production
        }
      }

      return null
    },

    onImageLoad() {
      // Log successful image loads for debugging
      // Removed event parameter and variables to fix ESLint unused var errors
      // const { target } = event
      // const itemId = target.getAttribute('data-item-id')
      // const item = itemId ? this.creatives.find(c => c.id === itemId) : null

      // Image load info removed for production
    },

    // Video detection method
    isVideoItem(item) {
      if (!item) return false

      // Video detection info removed for production

      // Check explicit type field
      if (item.type === 'video') {
        return true
      }

      // Check mime type from your database schema
      if (item.mime_type) {
        const mimeType = item.mime_type.toLowerCase()
        if (mimeType.startsWith('video/')) {
          return true
        }
      }

      // Check both filename and original_filename extensions
      const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.mpeg']

      if (item.filename) {
        const filename = item.filename.toLowerCase()
        const isVideo = videoExtensions.some(ext => filename.endsWith(ext))
        if (isVideo) {
          return true
        }
      }

      if (item.original_filename) {
        const originalFilename = item.original_filename.toLowerCase()
        const isVideo = videoExtensions.some(ext => originalFilename.endsWith(ext))
        if (isVideo) {
          return true
        }
      }

      // Check URL for video patterns
      if (item.url) {
        const url = item.url.toLowerCase()
        const isVideo = videoExtensions.some(ext => url.includes(ext))
        if (isVideo) {
          return true
        }

        // Check for simulation video URLs
        if (url.includes('simulation.tiktok.com') && url.includes('video')) {
          return true
        }
      }

      // Check file_path for video patterns
      if (item.file_path) {
        const filePath = item.file_path.toLowerCase()
        const isVideo = videoExtensions.some(ext => filePath.includes(ext))
        if (isVideo) {
          return true
        }
      }

      return false
    },

    // Video handling methods
    getVideoUrl(item) {
      if (!item) {
        return null
      }

      // Video URL info removed for production

      // Priority 1: Use metadata.tiktok_response.preview_url (actual TikTok CDN video)
      if (item.metadata?.tiktok_response?.preview_url) {
        return item.metadata.tiktok_response.preview_url
      }

      // Priority 2: Use file_path (TikTok CDN URL for videos)
      if (item.file_path && item.file_path.startsWith('http')) {
        return item.file_path
      }

      // Priority 3: Direct URL from API response
      if (item.url && item.url.startsWith('http')) {
        return item.url
      }

      // Priority 4: Try TikTok metadata video URLs
      if (item.metadata?.tiktok_response) {
        const tiktokResponse = item.metadata.tiktok_response

        // Try video_url first
        if (tiktokResponse.video_url) {
          return tiktokResponse.video_url
        }

        // Fall back to file_path in metadata
        if (tiktokResponse.file_path) {
          return tiktokResponse.file_path
        }

        // Try url in metadata
        if (tiktokResponse.url) {
          return tiktokResponse.url
        }
      }

      // Priority 5: Try various video URL properties
      const videoUrlProps = ['videoUrl', 'video_url', 'previewUrl', 'preview_url']
      const foundProp = videoUrlProps.find(prop => item[prop])
      if (foundProp) {
        return item[foundProp]
      }

      // Priority 6: If no video URL found, try to construct from API
      if (item.id) {
        const apiUrl = CreativeService.getVideoUrl(item.id)
        return apiUrl
      }

      return null
    },

    getVideoThumbnailUrl(item) {
      if (!item) {
        return this.getPlaceholderImage('video')
      }

      // Video thumbnail info removed for production

      // Priority 1: For videos, try to get thumbnail from TikTok API
      if (item.metadata?.tiktok_response) {
        const tiktokResponse = item.metadata.tiktok_response

        // Try various thumbnail properties
        const thumbnailProps = ['thumbnail_url', 'image_url', 'preview_image_url', 'cover_image_url']
        const foundProp = thumbnailProps.find(prop => tiktokResponse[prop])
        if (foundProp) {
          return tiktokResponse[foundProp]
        }
      }

      // Priority 2: Try standard thumbnail properties
      const thumbnailProps = ['thumbnailUrl', 'thumbnail_url', 'imageUrl', 'image_url', 'coverUrl', 'cover_url']
      const foundProp = thumbnailProps.find(prop => item[prop])
      if (foundProp) {
        return item[foundProp]
      }

      // Priority 3: Try API video thumbnail endpoint
      if (item.id) {
        const apiThumbnailUrl = CreativeService.getVideoThumbnail(item.id)
        return apiThumbnailUrl
      }

      // Priority 4: Fallback to video placeholder
      return this.getPlaceholderImage('video')
    },

    onVideoError(event) {
      const { target } = event
      // Video error info removed for production

      // Provide specific error messages based on error code
      let errorMessage = 'Unable to play video.'
      if (target.error) {
        switch (target.error.code) {
          case 1: // MEDIA_ERR_ABORTED
            errorMessage = 'Video playback was aborted.'
            break
          case 2: // MEDIA_ERR_NETWORK
            errorMessage = 'Video loading failed due to network error.'
            break
          case 3: // MEDIA_ERR_DECODE
            errorMessage = 'Video format not supported or corrupted.'
            break
          case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
            errorMessage = 'Video format not supported by browser.'
            break
          default:
            errorMessage = 'Unknown video playback error.'
        }
      }

      // Show user-friendly error message in console for now

      // Try to show toast but don't fail if component is missing
      try {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Video Playback Error',
            text: `${errorMessage} URL: ${target.src}`,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } catch (toastError) {
        // Toast error handling removed for production
      }
    },

    onVideoLoadStart() {
      // Removed event parameter to fix ESLint unused var error
      // const { target } = event
      // Video load info removed for production
    },

    onVideoThumbnailError(item, event) {
      // Video thumbnail error info removed for production

      // Replace with video placeholder immediately
      const { target } = event
      target.src = this.getPlaceholderImage('video')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './creatives.scss';
</style>
